/**
 * Admin 小区业绩考核
 */
import { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { api, ApiError } from '../../api/client';

type Assessment = {
  level_code: string;
  required: string;
  actual: string;
  pass: boolean;
};

type PerfResult = {
  user_id: number;
  username: string;
  level_code: string;
  target_level: string;
  left_usd?: string;
  right_usd?: string;
  small_market_perf: string;
  required: string;
  pass: boolean;
  assessments: Assessment[];
};

const TARGET_LEVELS = ['A1', 'A2', 'A3', 'A4', 'A5', 'S1', 'S2', 'S3', 'S4', 'S5'];

export function SmallMarketPerfPage() {
  const [userId, setUserId] = useState('');
  const [target, setTarget] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PerfResult | null>(null);
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [compare, setCompare] = useState<Record<string, unknown> | null>(null);

  async function load() {
    const raw = userId.trim();
    if (!raw) {
      message.warning('请输入玩家编号、用户 ID 或登录名');
      return;
    }
    setLoading(true);
    try {
      const res = await api.get<PerfResult>(
        `/api/admin/small-market-perf/${encodeURIComponent(raw)}`,
        {
          target_level: target,
        },
      );
      setData(res);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '查询失败');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Typography.Title level={4}>小区业绩考核</Typography.Title>
      <Typography.Paragraph type="secondary">
        小区 = 当前挂点下左右直挂的重算业绩 min(左, 右)。搬线后按个人 my_sum 从下往上重加，不再用搬家前的旧 team_sum。8/31 没有快照，对比不了那天。
      </Typography.Paragraph>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          style={{ width: 240 }}
          placeholder="玩家编号 / 用户 ID / 登录名"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          onPressEnter={() => void load()}
        />
        <Select
          allowClear
          placeholder="目标等级（默认当前）"
          style={{ width: 180 }}
          value={target}
          onChange={setTarget}
          options={TARGET_LEVELS.map((c) => ({ value: c, label: c }))}
        />
        <Button type="primary" loading={loading} onClick={() => void load()}>
          考核
        </Button>
      </Space>

      {data && (
        <Card>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="用户">
              {data.username} (#{data.user_id})
            </Descriptions.Item>
            <Descriptions.Item label="当前等级">{data.level_code}</Descriptions.Item>
            <Descriptions.Item label="目标等级">{data.target_level}</Descriptions.Item>
            <Descriptions.Item label="左区">{data.left_usd ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="右区">{data.right_usd ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="小区业绩">{data.small_market_perf}</Descriptions.Item>
            <Descriptions.Item label="所需阈值">{data.required}</Descriptions.Item>
            <Descriptions.Item label="结果">
              <Tag color={data.pass ? 'success' : 'error'}>{data.pass ? 'PASS' : 'FAIL'}</Tag>
            </Descriptions.Item>
          </Descriptions>

          <Table
            style={{ marginTop: 16 }}
            size="small"
            pagination={false}
            rowKey="level_code"
            dataSource={data.assessments}
            columns={[
              { title: '等级', dataIndex: 'level_code' },
              { title: '阈值', dataIndex: 'required' },
              { title: '实际', dataIndex: 'actual' },
              {
                title: '达标',
                dataIndex: 'pass',
                render: (v: boolean) => (
                  <Tag color={v ? 'success' : 'default'}>{v ? '是' : '否'}</Tag>
                ),
              },
            ]}
          />
        </Card>
      )}

      <Card title="月对比（已记录日期）" style={{ marginTop: 16 }}>
        <Space wrap>
          <DatePicker
            placeholder="起始日"
            onChange={(_, s) => setFrom(typeof s === 'string' ? s : '')}
          />
          <DatePicker
            placeholder="结束日"
            onChange={(_, s) => setTo(typeof s === 'string' ? s : '')}
          />
          <Button
            onClick={async () => {
              if (!userId.trim()) return;
              try {
                await api.post(`/api/admin/small-market-perf/${encodeURIComponent(userId.trim())}/snapshot`, {});
                message.success('已记录今日左右区');
              } catch (e) {
                message.error(e instanceof ApiError ? e.message : '记录失败');
              }
            }}
          >
            记录今日
          </Button>
          <Button
            type="primary"
            onClick={async () => {
              if (!userId.trim() || !from || !to) {
                message.warning('先选账户和两个日期');
                return;
              }
              try {
                const res = await api.get<Record<string, unknown>>(
                  `/api/admin/small-market-perf/${encodeURIComponent(userId.trim())}/compare`,
                  { from, to },
                );
                setCompare(res);
              } catch (e) {
                message.error(e instanceof ApiError ? e.message : '对比失败');
              }
            }}
          >
            对比
          </Button>
        </Space>
        {compare ? (
          <Typography.Paragraph style={{ marginTop: 12 }}>
            小区增量 {String(compare.delta_small ?? '—')} · 左 {String(compare.delta_left ?? '—')} · 右{' '}
            {String(compare.delta_right ?? '—')}
            {Array.isArray(compare.missing) && compare.missing.length
              ? ` · 缺记录 ${compare.missing.join(', ')}`
              : ''}
          </Typography.Paragraph>
        ) : null}
      </Card>
    </div>
  );
}
